import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth0 } from '@auth0/auth0-react';
import { getConfig } from '../config';

export type SocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface UseSocketOptions {
  botId: string;
  enabled?: boolean;
}

interface UseSocketReturn {
  socket: Socket | null;
  status: SocketStatus;
  retryAttempts: number;
  maxRetriesReached: boolean;
  retry: () => void;
}

const MAX_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second

export const useSocket = ({ botId, enabled = true }: UseSocketOptions): UseSocketReturn => {
  const { getAccessTokenSilently, getAccessTokenWithPopup, isAuthenticated } = useAuth0();
  const socketRef = useRef<Socket | null>(null);
  const retryAttemptsRef = useRef<number>(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxRetriesReachedRef = useRef<boolean>(false);
  const connectSocketRef = useRef<((isManualRetry?: boolean) => Promise<void>) | null>(null);
  const [status, setStatus] = useState<SocketStatus>('disconnected');
  const [retryAttempts, setRetryAttempts] = useState<number>(0);
  const [maxRetriesReached, setMaxRetriesReached] = useState<boolean>(false);
  const { socket: socketConfig } = getConfig();

  const connectSocket = useCallback(
    async (isManualRetry = false) => {
      // Reset retry attempts if manual retry
      if (isManualRetry) {
        retryAttemptsRef.current = 0;
        setRetryAttempts(0);
        maxRetriesReachedRef.current = false;
        setMaxRetriesReached(false);
      }

      // Check if max retries reached
      if (retryAttemptsRef.current >= MAX_RETRY_ATTEMPTS) {
        maxRetriesReachedRef.current = true;
        setMaxRetriesReached(true);
        setStatus('error');
        return;
      }

      try {
        setStatus('connecting');
        let token: string | undefined;

        try {
          token = await getAccessTokenSilently();
        } catch (error: any) {
          // Handle consent required error
          if (error?.error === 'consent_required' || error?.message?.includes('Consent required')) {
            console.warn('Consent required, attempting to get token with popup...');
            try {
              token = await getAccessTokenWithPopup();
            } catch (popupError) {
              console.error('Failed to get access token with popup:', popupError);
              throw popupError;
            }
          } else {
            throw error;
          }
        }

        if (!token) {
          console.error('Socket connection failed: No token available');
          // Handle failure inline
          retryAttemptsRef.current += 1;
          setRetryAttempts(retryAttemptsRef.current);

          if (retryAttemptsRef.current >= MAX_RETRY_ATTEMPTS) {
            maxRetriesReachedRef.current = true;
            setMaxRetriesReached(true);
            setStatus('error');
            console.log('Max retry attempts reached. Manual retry required.');
          } else {
            const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryAttemptsRef.current - 1);
            console.log(
              `Retrying connection in ${delay}ms (attempt ${retryAttemptsRef.current}/${MAX_RETRY_ATTEMPTS})`
            );

            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
            }

            retryTimeoutRef.current = setTimeout(() => {
              connectSocketRef.current?.(false);
            }, delay);
          }
          return;
        }

        if (!socketConfig.url) {
          console.error('Socket connection failed: Socket URL not configured');
          // Handle failure inline
          retryAttemptsRef.current += 1;
          setRetryAttempts(retryAttemptsRef.current);

          if (retryAttemptsRef.current >= MAX_RETRY_ATTEMPTS) {
            maxRetriesReachedRef.current = true;
            setMaxRetriesReached(true);
            setStatus('error');
            console.log('Max retry attempts reached. Manual retry required.');
          } else {
            const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryAttemptsRef.current - 1);
            console.log(
              `Retrying connection in ${delay}ms (attempt ${retryAttemptsRef.current}/${MAX_RETRY_ATTEMPTS})`
            );

            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
            }

            retryTimeoutRef.current = setTimeout(() => {
              connectSocketRef.current?.(false);
            }, delay);
          }
          return;
        }

        // Disconnect existing socket if any
        if (socketRef.current) {
          socketRef.current.removeAllListeners();
          socketRef.current.disconnect();
          socketRef.current = null;
        }

        console.log('Connecting to socket server at:', socketConfig.url);

        // Establish socket connection with Auth0 token and botId
        // Disable automatic reconnection, we'll handle it manually
        const socket = io(socketConfig.url, {
          auth: {
            token,
            botId,
          },
          transports: ['websocket', 'polling'],
          reconnection: false, // Disable automatic reconnection
        });

        socketRef.current = socket;

        socket.on('connect', () => {
          console.log('Socket connected:', socket.id);
          setStatus('connected');
          // Reset retry attempts on successful connection
          retryAttemptsRef.current = 0;
          setRetryAttempts(0);
          maxRetriesReachedRef.current = false;
          setMaxRetriesReached(false);
          // Clear any pending retry timeout
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
        });

        socket.on('disconnect', (reason) => {
          console.log('Socket disconnected:', reason);
          // Only set disconnected if not already in error state
          if (!maxRetriesReachedRef.current) {
            setStatus('disconnected');
          }
        });

        socket.on('connect_error', (error: any) => {
          console.error('Socket connection error:', error.message);
          console.error('Socket error details:', {
            message: error.message,
            description: error.description,
            context: error.context,
            type: error.type,
          });

          // Handle failure inline
          retryAttemptsRef.current += 1;
          setRetryAttempts(retryAttemptsRef.current);

          if (retryAttemptsRef.current >= MAX_RETRY_ATTEMPTS) {
            maxRetriesReachedRef.current = true;
            setMaxRetriesReached(true);
            setStatus('error');
            console.log('Max retry attempts reached. Manual retry required.');
          } else {
            const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryAttemptsRef.current - 1);
            console.log(
              `Retrying connection in ${delay}ms (attempt ${retryAttemptsRef.current}/${MAX_RETRY_ATTEMPTS})`
            );

            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
            }

            retryTimeoutRef.current = setTimeout(() => {
              connectSocketRef.current?.(false);
            }, delay);
          }
        });
      } catch (error) {
        console.error('Failed to establish socket connection:', error);
        // Handle failure inline
        retryAttemptsRef.current += 1;
        setRetryAttempts(retryAttemptsRef.current);

        if (retryAttemptsRef.current >= MAX_RETRY_ATTEMPTS) {
          maxRetriesReachedRef.current = true;
          setMaxRetriesReached(true);
          setStatus('error');
          console.log('Max retry attempts reached. Manual retry required.');
        } else {
          const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryAttemptsRef.current - 1);
          console.log(
            `Retrying connection in ${delay}ms (attempt ${retryAttemptsRef.current}/${MAX_RETRY_ATTEMPTS})`
          );

          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }

          retryTimeoutRef.current = setTimeout(() => {
            connectSocketRef.current?.(false);
          }, delay);
        }
      }
    },
    [botId, getAccessTokenSilently, getAccessTokenWithPopup, socketConfig.url]
  );

  // Store connectSocket in ref to avoid circular dependency
  useEffect(() => {
    connectSocketRef.current = connectSocket;
  }, [connectSocket]);

  const retry = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    // Clear any pending retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    connectSocketRef.current?.(true);
  }, []);

  useEffect(() => {
    if (!enabled || !isAuthenticated || !botId) {
      setStatus('disconnected');
      retryAttemptsRef.current = 0;
      setRetryAttempts(0);
      maxRetriesReachedRef.current = false;
      setMaxRetriesReached(false);
      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return;
    }

    connectSocket(false);

    // Cleanup: disconnect socket on unmount
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setStatus('disconnected');
    };
  }, [enabled, isAuthenticated, botId, connectSocket]);

  return {
    socket: socketRef.current,
    status,
    retryAttempts,
    maxRetriesReached,
    retry,
  };
};
